import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatChartDate } from "@/lib/intelligenceFormat";
import type { PharmacyDailyTrendPoint } from "@/api/intelligence";

type Props = {
	dailyTrend: PharmacyDailyTrendPoint[];
};

export function PharmacyTrendChart({ dailyTrend }: Props) {
	const chartData = useMemo(
		() => ({
			labels: dailyTrend.map((p) => formatChartDate(p.date)),
			datasets: [
				{
					label: "Revenue (₹)",
					data: dailyTrend.map((p) => p.revenue),
					borderColor: "rgb(79, 70, 229)",
					tension: 0.25,
					fill: false,
				},
				{
					label: "Issue value (₹)",
					data: dailyTrend.map((p) => p.issueValue),
					borderColor: "rgb(16, 185, 129)",
					tension: 0.25,
					fill: false,
				},
			],
		}),
		[dailyTrend]
	);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: "index" as const, intersect: false },
			plugins: { legend: { position: "bottom" as const } },
		}),
		[]
	);

	if (dailyTrend.length === 0) {
		return (
			<Card>
				<CardContent className="py-8 text-center text-sm text-muted-foreground">
					No trend data
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">Pharmacy revenue trend</CardTitle>
				<p className="text-xs text-muted-foreground">Accrual revenue by IST day</p>
			</CardHeader>
			<CardContent>
				<div className="h-[280px]">
					<Line data={chartData} options={options} />
				</div>
			</CardContent>
		</Card>
	);
}
